/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext } from '@msdyn365-commerce/core';
import { SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { reaction, computed, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import classnames from 'classnames';
import { UnitOfMeasure, humanizeUnitOfMeasure } from '../unit-of-measure';
import Switch from '../switch/switch';
import { getMinimumDecimals, getMostRelevantConversion, getRoundedFactor } from '../../shared-utils/unit-conversion-utils';

interface IQuantityProps {
    context: ICoreContext;
    locale: string;
    product: SimpleProduct | undefined;
    unitConversions?: UnitOfMeasureConversion[];
    currentCount?: number;
    allowZero?: boolean;
    disabled?: boolean;
    incrementGlyphClass?: string;
    decrementGlyphClass?: string;
    decrementButtonAriaLabel?: string;
    incrementButtonAriaLabel?: string;
    inputQuantityAriaLabel?: string;
    id?: string;
    min?: number;
    breakoff?: boolean;
    legacyBreakoff?: boolean;
    onChange?(newValue: number): void;
    onBreakoffChange?(enabled: boolean): void;
}

interface IQuantityState {
    currentInput: string | number;
    parsedCurrentInput: number;
    breakoff: boolean;
}

function getDecimalSeparator(locale: string) {
    const numberWithDecimalSeparator = 1.1;
    const separator = Intl.NumberFormat(locale)
        .formatToParts(numberWithDecimalSeparator)
        .find(part => part.type === 'decimal')
        ?.value;
    return separator ?? ',';
}

/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class ProductListOrderQuantity extends React.PureComponent<IQuantityProps, IQuantityState> {
    public static defaultProps: Partial<IQuantityProps> = {
        allowZero: true,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    @computed get minimum(): number {
        return (this.productMinimumQuantity || Number(!this.props.allowZero)) / this.multipleOf;
    }

    @computed get maximum(): number {
        const max = this.productMaximumQuantity || 999_999_999;
        return max / this.multipleOf;
    }

    @computed get multipleOf(): number {
        return this.state.breakoff ? 1 : this.props.product?.Behavior?.MultipleOfQuantity || 1;
    }

    @computed get productDefaultQuantity(): number | undefined {
        return this.props.product?.Behavior?.DefaultQuantity;
    }

    @computed get productMaximumQuantity(): number | undefined {
        return this.props.product?.Behavior?.MaximumQuantity;
    }

    @computed get productMinimumQuantity(): number | undefined {
        return this.props.product?.Behavior?.MinimumQuantity;
    }

    @computed get productMultipleOfQuantity(): number | undefined {
        return this.props.product?.Behavior?.MultipleOfQuantity;
    }

    @computed get total(): number {
        return this.state.parsedCurrentInput * this.multipleOf;
    }

    @computed get totalFormatted(): string {
        return this.total.toLocaleString(this.props.locale, {
            minimumFractionDigits: getMinimumDecimals(this.props.product?.DefaultUnitOfMeasure),
            maximumFractionDigits: 2,
            useGrouping: false
        });
    }

    @computed get showDecimals(): boolean {
        const units = [
            'kg',
            'Kilogram'
        ];
        const uom = this.props.product?.DefaultUnitOfMeasure;

        return !!uom && units.includes(uom);
    }

    constructor(props: IQuantityProps) {
        super(props);

        const { allowZero, currentCount, product } = this.props;
        let breakoff = false;
        let initialValue: number | string = '';

        if (product) {
            initialValue = currentCount || (allowZero ? 0 : this.productMinimumQuantity ?? 1);

            if (product.Behavior?.MultipleOfQuantity) {
                if (props.breakoff) {
                    breakoff = true;
                } else {
                    const breakoffState = this._getInitialBreakoff(initialValue, product);

                    if (breakoffState.factor) {
                        initialValue = breakoffState.factor;
                    } else {
                        breakoff = breakoffState.breakoff;
                    }
                }
            }
        }

        this.state = {
            currentInput: initialValue,
            parsedCurrentInput: typeof initialValue === 'number' ? initialValue : Number.parseFloat(initialValue) || 0,
            breakoff
        };
        this._clamp = this._clamp.bind(this);
        this._handleChange = this._handleChange.bind(this);
        this._validateQuantity = this._validateQuantity.bind(this);
        this._onToggleBreakoff = this._onToggleBreakoff.bind(this);
    }

    public componentDidMount(): void {
        when(
            () => this.props.product !== undefined && (this.state.currentInput === '' || this.state.currentInput === 0),
            () => {
                const { allowZero, currentCount, product } = this.props;
                const initialValue = currentCount || (allowZero ? 0 : this.productMinimumQuantity ?? 1);

                if (product?.Behavior?.MultipleOfQuantity) {
                    const multipler = product.Behavior.MultipleOfQuantity;
                    const factor = initialValue / multipler;
                    const remainder = initialValue % multipler;

                    if (remainder === 0) {
                        this.setState({ currentInput: factor });
                    } else {
                        this.setState({ breakoff: true });
                    }
                } else {
                    this.setState({ currentInput: initialValue });
                }
            }
        );

        reaction(
            () => this.props.breakoff,
            (breakoff) => {
                this.setState({ breakoff: Boolean(breakoff) });
            }
        );

        reaction(
            () => this.props.currentCount,
            () => {
                const currentInput = typeof this.state.currentInput === 'string'
                    ? Number.parseFloat(this.state.currentInput.replace(',', '.') || '0')
                    : this.state.currentInput;
                const oldQuantity = this._convertOutgoingQuantity(currentInput);
                const newQuantity = this._convertIncomingQuantity(this.props.currentCount ?? 0);

                // Only allow the change from props if it's meaningfully different
                if (this.props.currentCount !== oldQuantity) {
                    this.setState({
                        currentInput: newQuantity
                    });
                }
            }
        );

        reaction(
            () => this.state.breakoff,
            (newValue) => {
                if (newValue) {
                    const newInput = this.props.currentCount ?? 0;

                    this.setState({
                        currentInput: newInput,
                        parsedCurrentInput: newInput
                    });
                } else {
                    // const multiplier = this.productMultipleOfQuantity || 1;
                    const multiplier = this.multipleOf;
                    const newInput = this.state.parsedCurrentInput / multiplier;

                    this.setState({
                        currentInput: newInput,
                        parsedCurrentInput: newInput
                    });
                }
            }
        );

        reaction(
            () => this.state.currentInput,
            () => {
                this.setState({
                    currentInput: this.showDecimals ? this.state.currentInput.toLocaleString(this.props.locale, {
                        minimumFractionDigits: getMinimumDecimals(this.props.product?.DefaultUnitOfMeasure),
                        maximumFractionDigits: 2,
                        useGrouping: false
                    }).replace('.', ',') : this.state.currentInput,
                    parsedCurrentInput: Number.parseFloat(this.state.currentInput.toString().replace(',', '.') || '0')
                });
            }
        );
    }

    public render(): JSX.Element {
        const { allowZero, context, product } = this.props;
        const disabled = this.state.parsedCurrentInput === (allowZero  ? 0 : this.minimum);
        const incDisabled = this.state.parsedCurrentInput === this.maximum;
        const currentValue = this.state.currentInput;
        const baseClassName = 'dlvry-quantity';
        const breakoffEnabled = !!product?.Behavior?.MultipleOfQuantity;
        const miniBreakoff = !this.props.legacyBreakoff;

        let extraClass = '';
        if (disabled) {
            // The quantity has reached its boundaries (max or min)
            extraClass = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClass = 'transition';
        }
        const getChangeHandler = (action: 'increment' | 'decrement') => () => {
            let amount = action === 'increment' ? 1 : -1;

            if (action === 'increment' && this.state.parsedCurrentInput === 0 && this.productDefaultQuantity) {
                amount = this.productDefaultQuantity / this.multipleOf;
            }

            const calcTotal = (change: number): number => {
                const newAmount = this.state.parsedCurrentInput + change;
                return this.multipleOf * newAmount;
            };

            let newTotal = calcTotal(amount);

            if (action === 'increment' && newTotal > 0) {
                if (this.productMinimumQuantity && newTotal < this.productMinimumQuantity) {
                    amount = (this.productMinimumQuantity - this.total) / this.multipleOf;
                }

                newTotal = calcTotal(amount);

                if (this.productMaximumQuantity && newTotal > this.productMaximumQuantity) {
                    amount = (this.productMaximumQuantity - this.total) / this.multipleOf;
                }
            }

            if (action === 'decrement'  && newTotal > 0) {
                if (this.productMinimumQuantity && newTotal < this.productMinimumQuantity) {
                    if (allowZero) {
                        amount = -this.state.parsedCurrentInput;
                    } else {
                        amount = this.minimum - this.state.parsedCurrentInput;
                    }
                }
                if (this.productMaximumQuantity && newTotal > this.productMaximumQuantity) {
                    amount = this.maximum - this.state.parsedCurrentInput;
                }
            }

            this._onIncrementalChange(amount);
        };

        return (
            <div className={classnames(baseClassName, { 'mini': miniBreakoff })}>
                {!miniBreakoff && (
                    <Switch
                        className={classnames('breakoff-switch', { 'hidden': !product?.Behavior?.MultipleOfQuantity })}
                        label='Anbrekk'
                        checked={this.state.breakoff}
                        onChange={product?.Behavior?.MultipleOfQuantity ? this._onToggleBreakoff : undefined} />
                )}
                <div className={`${baseClassName}__quantity-wrapper`}>
                    <div className={classnames(`${baseClassName}__quantity-input-wrapper`, { disabled: this.props.disabled })}>
                        {miniBreakoff && breakoffEnabled && (
                            <>
                                <Switch
                                    className={classnames('breakoff-switch mini', { 'hidden': !product?.Behavior?.MultipleOfQuantity })}
                                    label='Anbrekk'
                                    mini
                                    miniLabel='A'
                                    checked={this.state.breakoff}
                                    onChange={product?.Behavior?.MultipleOfQuantity ? this._onToggleBreakoff : undefined} />

                                <span className={`${baseClassName}__product-unit`}>
                                    {`${this.multipleOf} `}
                                    &nbsp;x
                                </span>
                            </>
                        )}
                        <button
                            disabled={this.props.disabled || disabled}
                            title={disabled ? '' : this.props.decrementButtonAriaLabel}
                            className={`${baseClassName}__quantity__controls decrement ${extraClass}`}
                            onClick={getChangeHandler('decrement')}
                            aria-hidden
                            aria-label={`${this.props.decrementButtonAriaLabel}`}
                            tabIndex={-1}
                            color='secondary'
                        />
                        <input
                            /* Handle input as a string to avoid the issues with commas and dots */
                            type='text'
                            className={`${baseClassName}__quantity-input`}
                            pattern='[0-9]*'
                            value={typeof currentValue === 'string' ? currentValue : currentValue.toLocaleString(this.props.locale, { useGrouping: false })}
                            onBlur={this._validateQuantity}
                            onChange={this._handleChange}
                            onKeyPress={(e) => {
                                const NUMBER_DOT_COMMA = /^[\d,.]*$/;
                                const fieldValue = (e.target as any).value;
                                const fieldHasCommaOrDot = fieldValue.includes('.') || fieldValue.includes(',');
                                const keyIsCommaOrDot = e.key === '.' || e.key === ',';

                                if (!NUMBER_DOT_COMMA.test(e.key) || (keyIsCommaOrDot && fieldHasCommaOrDot)) {
                                    e.preventDefault();
                                }

                                const separator = getDecimalSeparator(this.props.locale);
                                const replace = separator === '.' ? ',' : '.';
                                (e as any).target.value = fieldValue.replace(replace, separator);
                            }}
                            aria-live='polite'
                            aria-label={`${this.props.inputQuantityAriaLabel}`}
                            role='spinbutton'
                            aria-valuemin={1}
                            aria-valuemax={this.maximum}
                            aria-valuenow={Number.parseFloat(currentValue.toString()) || 0}
                            ref={this.inputRef}
                            disabled={this.props.disabled}
                            data-nomodalcss
                        />
                        <button
                            disabled={this.props.disabled || incDisabled}
                            title={incDisabled ? '' : this.props.incrementButtonAriaLabel}
                            className={`${baseClassName}__quantity__controls increment ${incDisabled ? 'disabled' : ''}`}
                            onClick={getChangeHandler('increment')}
                            aria-hidden
                            aria-label={`${this.props.incrementButtonAriaLabel}`}
                            tabIndex={-1}
                            color='secondary'
                        />
                    </div>
                    <span className={`${baseClassName}__product-unit-measure`}>
                        = {this.totalFormatted}&nbsp;
                        <UnitOfMeasure context={context} simpleProduct={product} />
                    </span>
                </div>
                <div className={`${baseClassName}__mobile-quantity-info`}>
                    1 =&nbsp;
                    {`${this.multipleOf} `}
                    <UnitOfMeasure context={context} simpleProduct={product} />
                </div>
                <div className={`${baseClassName}__unit-conversion-wrapper`}>
                    {this._getUnitOfMeasureConversion()}
                </div>
            </div>
        );
    }

    private _getInitialBreakoff = (initialValue: number, product: SimpleProduct): { factor?: number, breakoff: boolean } => {
        const multiplier = product.Behavior?.MultipleOfQuantity || 1;
        const factor = Math.round(((initialValue / multiplier) + Number.EPSILON) * 1000) / 1000;
        const remainder = (initialValue * 1000) % (multiplier * 1000);

        if (remainder === 0) {
            return { factor, breakoff: false };
        } else {
            return { breakoff: true };
        }
    };

    private _getUnitOfMeasureConversion = (): JSX.Element | undefined => {
        if (!this.props.unitConversions || !this.props.product?.DefaultUnitOfMeasure) {
            return undefined;
        }

        const conversions = this.props.unitConversions?.filter(x => x.FromUnitOfMeasureSymbol === this.props.product?.DefaultUnitOfMeasure);
        const conversion = getMostRelevantConversion(conversions);

        if (!conversion) {
            return undefined;
        }

        const factor = getRoundedFactor(conversion).toLocaleString(this.props.locale, {
            minimumFractionDigits: getMinimumDecimals(conversion.ToUnitOfMeasureSymbol),
            maximumFractionDigits: 2,
            useGrouping: false
        });
        const defaultUOM = humanizeUnitOfMeasure(this.props.context, this.props.product?.DefaultUnitOfMeasure);
        const conversionUOM = humanizeUnitOfMeasure(this.props.context, conversion.ToUnitOfMeasureSymbol).toLocaleLowerCase();

        return (
            <div>
                1 {defaultUOM} = {`${factor} ${conversionUOM}`}
            </div>
        );
    };

    private _convertIncomingQuantity = (quantity: number): number => {
        return quantity / this.multipleOf;
    };

    private _convertOutgoingQuantity = (quantity: number): number => {
        const outgoing = quantity * this.multipleOf;

        // We need to always round to nearest 2 decimal places to avoid floating point errors in quantity validation
        return Math.round((outgoing + Number.EPSILON) * 1000) / 1000;
    };

    private _onToggleBreakoff = (checked: boolean): void => {
        this.setState({ breakoff: checked });
        this.props.onBreakoffChange?.(checked);
    };

    private _onIncrementalChange(amount: number): void {
        const updatedQuantity = this.state.parsedCurrentInput + amount;

        if (this._isValidNewQuantity(updatedQuantity)) {
            this.props.onChange?.(this._convertOutgoingQuantity(updatedQuantity));
        }
    }

    private readonly _clamp = (total: number): number => {
        if (this.productMinimumQuantity && total >= 1 && total < this.productMinimumQuantity) {
            return this.productMinimumQuantity / this.multipleOf;
        } else if (this.productMaximumQuantity && total > this.productMaximumQuantity) {
            return this.productMaximumQuantity / this.multipleOf;
        } else {
            return total / this.multipleOf;
        }
    };

    private readonly _isValidNewQuantity = (newQuantity: number): boolean => {
        if (newQuantity === 0 && this.props.allowZero) {
            return true;
        }
        return !isNaN(newQuantity) && newQuantity >= this.minimum && newQuantity <= this.maximum;
    };

    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let newValue: string | number = e.target.value;

        e.persist();
        this.setState({ currentInput: newValue }, () => {
            if (typeof newValue === 'string') {
                newValue = Number.parseFloat(newValue.replace(',', '.'));
            }

            if (this._isValidNewQuantity(newValue)) {
                this.props.onChange?.(this._convertOutgoingQuantity(newValue));
            }
        });
    }

    private _validateQuantity(e: React.FocusEvent<HTMLInputElement>): void {
        const currentValue = Number.parseFloat(e.target.value.replace(',', '.') || '0');
        const total = this.multipleOf * currentValue;
        const newValue = Math.max(this._clamp(total) ?? 0, Number(!this.props.allowZero));

        e.persist();
        this.setState({ currentInput: newValue }, () => {
            this.props.onChange?.(this._convertOutgoingQuantity(newValue));
        });
    }
}
